import { I18N_LOCALES } from "@/i18n";
import locale from "locale-codes";

/**
 * Retrieves all available locales and filters out those that contain a dash,
 * effectively returning only language-only locale tags (e.g., "en", "fr").
 * Removes duplicates based on the locale tag.
 *
 * @returns {locale.ILocale[]} An array of unique language-only locales.
 */
export const getFilteredLocales = (): locale.ILocale[] => {
  // Get locals with filtered out country codes & removed duplicates
  const allLocales = locale.all;
  const languageOnly = allLocales.filter((loc) => !loc.tag.includes("-"));
  const uniqueLanguageCodes = [...new Set(languageOnly)];

  return uniqueLanguageCodes;
};

/**
 * Retrieves the filtered locales that are defined in the application's
 * internationalisation (i18n) configuration.
 *
 * @returns {locale.ILocale[]} An array of locales available in the i18n config.
 */
export const getAvailableI18nLocales = (): locale.ILocale[] => {
  // Return only locales that are in the i18n config
  const allLocales = locale.all;
  return allLocales.filter((loc) => I18N_LOCALES.includes(loc.tag));
};

/**
 * Retrieves all available regions for the currently selected language
 */
export const getRegionOptionsForLanguage = (languageTag: string): locale.ILocale[] => {
  const allLocales = locale.all;
  const langPrefix = languageTag.split("-")[0];

  // Include locales with lang-region format that start with
  // selected language (e.g., English = en-US, en-GB, ...)
  const allRegions = allLocales.filter((loc) => {
    const parts = loc.tag.split("-");
    return parts.length > 1 && parts[0] === langPrefix;
  });

  // Include the base language option at the start (without region)
  const baseLanguage = locale.where("tag", languageTag.split("-")[0]);
  if (baseLanguage) {
    return [baseLanguage, ...allRegions];
  }
  return allRegions;
};
